/* eslint-disable max-len */
/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import {
    assertEvent,
    assign,
    raise,
    setup,
} from 'xstate';
import { produce } from 'immer';
import {
    PropertyRequestMinimumCreationDetails, PropertyRequestStateWorkflow, SearchType, StateShortName,
} from '@acp/shared';
import { propertyRequestManagerActors } from '../../machine-actors';

type AddressParts = {
    streetNumber: string;
    streetName: string;
    suburb: string;
    postcode: string;
    state: StateShortName;
    unitNumber?: string | null | undefined;
    streetType?: string | null | undefined;
};

type PropertyRequestCreatorEvents =
 { type: 'ADDRESS_UNKNOWN' }
 | { type: 'SET_ADDRESS_QUERY'; addressQuery: string; addressSearchType: SearchType }
 | { type: 'SELECT_ADDRESS'; addressId: number; fullAddress: string }
 | { type: 'SELECT_CREATION_TOKEN'; creationToken: string; fullAddress: string; addressParts: AddressParts | null }
 | { type: 'CREATE_NEW_ADDRESS' }
 | { type: 'SELECT_PROPERTY_ID'; propertyId: number; fullAddress: string; complianceAgencyId?: number }
 | { type: 'SELECT_WORKFLOW'; workflow: PropertyRequestStateWorkflow }
 | { type: 'BACK' }
 | { type: 'OPEN'; prefilledAddress: string; excludeEnrolledProperties?: boolean }
 | { type: 'OPEN'; propertyId: number; fullAddress: string }
 | { type: 'OPEN' }
 | { type: 'CLOSE' }
 | { type: 'FORCE_CLOSE' }
 | { type: 'BACK_TO_WORKFLOW_SELECTION' }
 | { type: 'CANCEL_CLOSE' }
 | { type: 'DISABLE_CLOSE_CONFIRMATION' }
 | { type: 'RESET' }
 | { type: 'FATAL_ERROR'; message: string };

const propertyRequestCreatorMachine = setup({
    types: {
        events: {} as PropertyRequestCreatorEvents,
        context: {} as {
            preselectedPropertyId: number | null;
            preselectedAddressId: number | null;
            requestedComplianceAgencyId?: number;
            supportedPayloadVersions: string[];
            searchAddressQuery: string;
            searchAddressType: SearchType;
            fullAddress: string | null;
            creationDetails: PropertyRequestMinimumCreationDetails | null;
            fatalErrorMessage: string | null;
            availableWorkflows: PropertyRequestStateWorkflow[];
            selectedWorkflow: PropertyRequestStateWorkflow | null;
            selectedPayloadVersion: string | null;
            hasManuallyCreatedAddress: boolean;
            manualAddressParts: AddressParts | null;
            isModalOpen: boolean;
            propertyRequestId: string | null;
            showCloseConfirmation: boolean;
            isCloseConfirmationEnabled: boolean;
            excludeEnrolledProperties: boolean;
            /**
             * When true, a single available workflow auto-advances from selectWorkflow (legacy redirect or create).
             * False after BACK_TO_WORKFLOW_SELECTION so the request-type screen stays visible.
             */
            autoAdvanceSingleWorkflow: boolean;
        },
        input: {} as {
            supportedPayloadVersions: string[];
        },
    },
    actors: propertyRequestManagerActors,
    actions: {
        navigateToLegacyJobOrderPage: (_, _params: { url: string }) => {},
        onPropertyRequestCreated: (_, _params: { propertyRequestId: string }) => {},
        onFatalError: (_, _params: { message: string }) => {},

        resetAll: assign(({ context }) => produce(context, (draft) => {
            draft.preselectedPropertyId = null;
            draft.preselectedAddressId = null;
            draft.creationDetails = null;
            draft.fullAddress = null;
            draft.fatalErrorMessage = null;
            draft.selectedPayloadVersion = null;
            draft.requestedComplianceAgencyId = undefined;
            draft.hasManuallyCreatedAddress = false;
            draft.manualAddressParts = null;
            draft.isModalOpen = false;
            draft.searchAddressQuery = '';
            draft.searchAddressType = SearchType.INTERNAL;
            draft.propertyRequestId = null;
            draft.showCloseConfirmation = false;
            draft.isCloseConfirmationEnabled = true;
            draft.excludeEnrolledProperties = false;
            draft.autoAdvanceSingleWorkflow = true;
        })),

        reset: assign((
            { context }
        ) => produce(context, (draft) => {
            // draft.availableWorkflows = [];
            // draft.selectedWorkflow = null;
            draft.selectedPayloadVersion = null;
            draft.requestedComplianceAgencyId = undefined;
            draft.propertyRequestId = null;
            // draft.requestorDetails = {
            //     zendeskTicketId: undefined,
            //     requestedFullName: undefined,
            //     requestedEmailAddress: undefined,
            //     requestedContactTypeId: undefined,
            // };

            draft.creationDetails = null;
            draft.fullAddress = null;
            draft.isModalOpen = false;
            draft.showCloseConfirmation = false;
            draft.isCloseConfirmationEnabled = true;
            draft.autoAdvanceSingleWorkflow = true;
        })),

        setSearchAddressQuery: assign((
            { context },
            params: { addressQuery: string; addressSearchType: SearchType }
        ) => produce(context, (draft) => {
            draft.searchAddressQuery = params.addressQuery;
            draft.searchAddressType = params.addressSearchType;
        })),

        selectExistingProperty: assign((
            { context },
            params: { propertyId: number; fullAddress: string; complianceAgencyId?: number }
        ) => produce(context, (draft) => {
            draft.creationDetails = {
                type: 'existing-property',
                compliancePropertyId: params.propertyId,
            };

            draft.fullAddress = params.fullAddress;

            draft.requestedComplianceAgencyId = params.complianceAgencyId;
        })),

        selectExistingAddress: assign((
            { context },
            params: { addressId: number; fullAddress: string }
        ) => produce(context, (draft) => {
            draft.creationDetails = {
                type: 'existing-address',
                complianceAddressId: params.addressId,
            };

            draft.fullAddress = params.fullAddress;
        })),

        selectCreationToken: assign((
            { context },
            params: {
                creationToken: string;
                fullAddress: string;
                addressParts: AddressParts | null;
            }
        ) => produce(context, (draft) => {
            draft.creationDetails = {
                type: 'new-address',
                creationToken: params.creationToken,
            };

            draft.fullAddress = params.fullAddress;

            draft.hasManuallyCreatedAddress = params.addressParts !== null;
            draft.manualAddressParts = params.addressParts;
        })),

        selectWorkflow: assign((
            { context },
            params: { workflow: PropertyRequestStateWorkflow }
        ) => produce(context, (draft) => {
            draft.selectedWorkflow = params.workflow;

            // We want to select the first supported payload version
            // in our list of supported versions
            // This way the order dictates a priority list

            const payloadVersionId = params.workflow.temporalWorkflow
                .payloadVersionsForNewExecutions
                .find(
                    (x) => context.supportedPayloadVersions.includes(x)
                );

            // Pretty much unreachable since the available workflows was guaranteed to include
            // only ones that we support
            if (payloadVersionId === undefined) {
                throw new Error('Workflow selected has no supported payload versions by this app');
            }

            draft.selectedPayloadVersion = payloadVersionId;

            draft.autoAdvanceSingleWorkflow = true;
        })),

        setAvailableWorkflows: assign((
            { context },
            params: { availableWorkflows: PropertyRequestStateWorkflow[] }
        ) => produce(context, (draft) => {
            // Reset the selected workflow if we're setting available workflows
            draft.selectedWorkflow = null;

            // Only workflows that we support are available
            // Which can either be ones that have a supported payload version
            // Or ones that use the legacy job order page
            const supportedWorkflows = params.availableWorkflows
                .filter(
                    (workflow) => {
                        const hasSupportedPayloadVersion = workflow.temporalWorkflow
                            .payloadVersionsForNewExecutions
                            .some(
                                (x) => context.supportedPayloadVersions.includes(x)
                            );

                        const isLegacyWorkflow = workflow.useLegacyJobOrderPage;

                        return hasSupportedPayloadVersion || isLegacyWorkflow;
                    }
                );

            draft.availableWorkflows = supportedWorkflows;
            draft.autoAdvanceSingleWorkflow = true;
        })),
    },
    guards: {

    },
}).createMachine({
    id: 'propertyRequestCreator',
    initial: 'idle',
    context: ({ input }) => ({
        preselectedPropertyId: null,
        preselectedAddressId: null,
        propertyRequestId: null,
        creationDetails: null,
        fullAddress: null,
        fatalErrorMessage: null,
        availableWorkflows: [],
        selectedWorkflow: null,
        supportedPayloadVersions: input.supportedPayloadVersions,
        selectedPayloadVersion: null,
        searchAddressQuery: '',
        searchAddressType: SearchType.INTERNAL,
        hasManuallyCreatedAddress: false,
        manualAddressParts: null,
        isModalOpen: false,
        showCloseConfirmation: false,
        isCloseConfirmationEnabled: true,
        excludeEnrolledProperties: false,
        autoAdvanceSingleWorkflow: true,
    }),
    on: {
        FATAL_ERROR: {
            description: 'A fatal error occurred',
            actions: [assign(({ context, event }) => produce(context, (draft) => {
                assertEvent(event, 'FATAL_ERROR');
                draft.fatalErrorMessage = event.message;
            })), {
                type: 'onFatalError',
                params: ({ event }) => ({ message: event.message }),
            }],
            target: '.fatalError',
        },
        FORCE_CLOSE: {
            actions: 'reset',
            target: '.idle',
            reenter: true,
        },
        CANCEL_CLOSE: {
            actions: assign(({ context }) => produce(context, (draft) => {
                draft.showCloseConfirmation = false;
            })),
        },
        DISABLE_CLOSE_CONFIRMATION: {
            actions: assign(({ context }) => produce(context, (draft) => {
                draft.isCloseConfirmationEnabled = false;
            })),
        },
    },
    states: {
        idle: {
            entry: {
                type: 'resetAll',
            },
            on: {
                OPEN: [{
                    target: 'loadPreflightWorkflows',
                    guard: ({ event }) => 'propertyId' in event,
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.isModalOpen = true;

                        if ('propertyId' in event) {
                            draft.preselectedPropertyId = event.propertyId;
                            draft.fullAddress = event.fullAddress;
                            draft.creationDetails = {
                                type: 'existing-property',
                                compliancePropertyId: event.propertyId,
                            };
                        }
                    })),
                }, {
                    target: 'selectAddress',
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.isModalOpen = true;

                        if ('prefilledAddress' in event) {
                            draft.searchAddressQuery = event.prefilledAddress;
                            draft.searchAddressType = SearchType.INTERNAL;
                            draft.excludeEnrolledProperties = event.excludeEnrolledProperties ?? false;
                        }
                    })),
                }],
            },
        },
        selectAddress: {
            meta: {
                heading: 'Select an address',
                headerTitle: 'New work order',
            },
            always: [
                {
                    guard: ({ context }) => context.preselectedPropertyId !== null,
                    target: 'getExistingFullAddress',
                },
                {
                    guard: ({ context }) => context.preselectedAddressId !== null,
                    target: 'getExistingFullAddress',
                },
            ],
            on: {
                SET_ADDRESS_QUERY: {
                    actions: {
                        type: 'setSearchAddressQuery',
                        params: ({ event }) => event,
                    },
                },
                SELECT_PROPERTY_ID: {
                    // TODO: change this step
                    target: 'loadPreflightWorkflows',
                    actions: {
                        type: 'selectExistingProperty',
                        params: ({ event }) => event,
                    },
                },
                SELECT_ADDRESS: {
                    target: 'loadPreflightWorkflows',
                    actions: {
                        type: 'selectExistingAddress',
                        params: ({ event }) => event,
                    },
                },
                SELECT_CREATION_TOKEN: {
                    target: 'loadPreflightWorkflows',
                    actions: {
                        type: 'selectCreationToken',
                        params: ({ event }) => event,
                    },
                },
                CREATE_NEW_ADDRESS: {
                    target: 'createAddress',
                },
                CLOSE: {
                    actions: raise({ type: 'FORCE_CLOSE' }),
                },
            },
        },
        createAddress: {
            meta: {
                heading: 'Manually add an address',
                headerTitle: 'New work order',
            },
            on: {
                SELECT_CREATION_TOKEN: {
                    target: 'loadPreflightWorkflows',
                    actions: {
                        type: 'selectCreationToken',
                        params: ({ event }) => ({
                            creationToken: event.creationToken,
                            fullAddress: event.fullAddress,
                            addressParts: event.addressParts,
                        }),
                    },
                },
                BACK: {
                    target: 'selectAddress',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.hasManuallyCreatedAddress = false;
                        draft.manualAddressParts = null;
                    })),
                },
                CLOSE: {
                    actions: raise({ type: 'FORCE_CLOSE' }),
                },
            },
        },
        getExistingFullAddress: {
            invoke: {
                src: 'getExistingFullAddress',
                input: ({ context }) => ({
                    preselectedAddressId: context.preselectedAddressId,
                    preselectedPropertyId: context.preselectedPropertyId,
                }),
                onDone: {
                    target: 'loadPreflightWorkflows',
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.creationDetails = event.output!.creationDetails;
                        draft.fullAddress = event.output!.fullAddress;
                    })),
                },
                onError: {
                    actions: raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                },
            },
        },
        loadPreflightWorkflows: {
            meta: {
                heading: 'Requesting agency',
                headerTitle: 'Create Property Request',
            },
            invoke: {
                src: 'getPreflightWorkflows',
                input: ({ context }) => ({
                    creationDetails: context.creationDetails!,
                }),
                onDone: {
                    target: 'selectWorkflow',
                    actions: {
                        type: 'setAvailableWorkflows',
                        params: ({ event }) => ({ availableWorkflows: event.output }),
                    },
                },
                onError: {
                    actions: raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                },
            },
        },
        selectWorkflow: {
            meta: {
                heading: 'Request type',
                headerTitle: 'Create Property Request',
            },
            always: [
                {
                    guard: ({ context }) => context.availableWorkflows.length === 1
                        && context.availableWorkflows[0].useLegacyJobOrderPage
                        && context.autoAdvanceSingleWorkflow,
                    target: 'idle',
                    reenter: true,
                    actions: {
                        type: 'navigateToLegacyJobOrderPage',
                        params: ({ context }) => ({
                            url: context.availableWorkflows[0].legacyJobOrderUrl!,
                        }),
                    },
                },
                {
                    guard: ({ context }) => context.availableWorkflows.length === 1
                        && context.autoAdvanceSingleWorkflow,
                    target: 'createPropertyRequest',
                    actions: {
                        type: 'selectWorkflow',
                        params: ({ context }) => ({
                            workflow: context.availableWorkflows[0],
                        }),
                    },
                },
            ],
            on: {
                SELECT_WORKFLOW: {
                    target: 'createPropertyRequest',
                    actions: {
                        type: 'selectWorkflow',
                        params: ({ event }) => event,
                    },
                },
                BACK: [
                    {
                        guard: ({ context }) => context.preselectedPropertyId !== null,
                        target: 'idle',
                        reenter: true,
                    },
                    {
                        target: 'selectAddress',
                    },
                ],
            },
        },
        noWorkflowsAvailable: {
            type: 'final',
        },
        legacyJobOrderPage: {
            type: 'final',
        },
        createPropertyRequest: {
            meta: {
                heading: 'Confirm request',
                headerTitle: 'Create Property Request',
            },
            invoke: {
                src: 'createPropertyRequest',
                input: ({ context }) => ({
                    requestedComplianceAgencyId: context.requestedComplianceAgencyId!,
                    creationDetails: context.creationDetails!,
                    stateWorkflowId: context.selectedWorkflow!.id,
                    payloadVersionId: context.selectedPayloadVersion!,
                }),
                onDone: {
                    target: 'editPropertyRequest',
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.propertyRequestId = event.output.id;
                    })),
                },
                onError: {
                    actions: raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                },
            },
        },
        editPropertyRequest: {
            on: {
                BACK_TO_WORKFLOW_SELECTION: {
                    target: 'selectWorkflow',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.propertyRequestId = null;
                        draft.selectedWorkflow = null;
                        draft.selectedPayloadVersion = null;
                        draft.autoAdvanceSingleWorkflow = false;
                    })),
                },
                CLOSE: [
                    {
                        guard: ({ context }) => !context.isCloseConfirmationEnabled,
                        actions: raise({ type: 'FORCE_CLOSE' }),
                    },
                    {
                        actions: assign(({ context }) => produce(context, (draft) => {
                            draft.showCloseConfirmation = true;
                        })),
                    },
                ],
            },
        },
        fatalError: {
            meta: {
                heading: 'An unexpected error occurred',
                headerTitle: 'Fatal error',
            },
            type: 'final',
        },
    },
});

export { propertyRequestCreatorMachine, PropertyRequestCreatorEvents };
